import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SessionStatusBadge } from '../components/training/SessionStatusBadge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { CalendarDays, ClipboardCheck, Plus, ChevronLeft, ChevronRight, Pencil, Trash2, Dumbbell, Activity, Heart, Star, Repeat, CheckSquare, Square } from 'lucide-react';
import AthleteContextHeader from '../components/AthleteContextHeader';
import TargetAudienceSelector from '../components/TargetAudienceSelector';
import AthleteSearchFilter from '../components/AthleteSearchFilter';
import { AthleteAvatar } from '../components/AthleteAvatar';
import { DatePicker } from '../components/ui/date-picker';
import RepeatSessionsWizard from '../components/RepeatSessionsWizard';
import RepeatInformationCard from '../components/RepeatInformationCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_TYPES = [
  { value: 'pitch', label: 'Pitch Training', icon: Activity, color: 'bg-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  { value: 'gym', label: 'Gym / Physical', icon: Dumbbell, color: 'bg-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  { value: 'rehab', label: 'Rehab / Recovery', icon: Heart, color: 'bg-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800' },
  { value: 'special', label: 'Special Event', icon: Star, color: 'bg-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
];

const FOCUS_OPTIONS = ['Tactical', 'Technical', 'Conditioning', 'Possession', 'Defensive Shape', 'Set Pieces', 'Physical', 'Recovery', 'Mental', 'General', 'Friendly Match', 'Workshop', 'Other'];

const getTypeConfig = (type) => SESSION_TYPES.find(t => t.value === type) || SESSION_TYPES[0];

const getWeekDates = (baseDate) => {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
};

const formatDateKey = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Session card for calendar — defined at module scope to prevent remounting when parent re-renders.
const SessionPill = ({ session, onClick, selectionMode = false, selected = false, onToggleSelect }) => {
  const cfg = getTypeConfig(session.session_type);
  const handleClick = (e) => {
    if (selectionMode) {
      e.stopPropagation();
      onToggleSelect?.(session);
    } else {
      onClick?.();
    }
  };
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-left cursor-pointer transition-all hover:shadow-sm ${cfg.bg} ${cfg.border} ${selected ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}
      onClick={handleClick}
      data-testid={`session-pill-${session.id}`}
    >
      {selectionMode && (
        <span className="flex-shrink-0" data-testid={`session-select-${session.id}`}>
          {selected ? <CheckSquare className="w-3.5 h-3.5 text-emerald-600" /> : <Square className="w-3.5 h-3.5 text-gray-400" />}
        </span>
      )}
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.color}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] font-medium truncate ${cfg.text}`}>{session.title}</p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500">{session.time?.slice(0, 5)}{session.training_focus !== 'General' ? ` · ${session.training_focus}` : ''}</p>
      </div>
      {session.recurrence_rules?.recurrence_group_id && (
        <Repeat
          className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0 opacity-70"
          strokeWidth={2.25}
          aria-label="Created using Repeat Sessions"
          title="Created using Repeat Sessions"
          data-testid={`recurrence-badge-${session.id}`}
        />
      )}
      {session.target_type === 'custom' && (
        <Badge className={`text-[8px] px-1 py-0 ${cfg.text} bg-transparent border-0`}>
          {(session.target_squads || []).join(',') || `${(session.target_athletes || []).length}p`}
        </Badge>
      )}
    </div>
  );
};

// Phase 88B — all nine attendance statuses with concise staff-facing tooltips.
const ATTENDANCE_STATUS_OPTIONS = [
  { key: 'present', label: 'Present', active: 'bg-emerald-600 hover:bg-emerald-700 text-white', tip: 'Participated normally.' },
  { key: 'late', label: 'Late', active: 'bg-teal-600 hover:bg-teal-700 text-white', tip: 'Attended but arrived late.' },
  { key: 'rehab', label: 'Rehab', active: 'bg-cyan-600 hover:bg-cyan-700 text-white', tip: 'Attended and completed rehabilitation activity.' },
  { key: 'modified', label: 'Modified', active: 'bg-sky-600 hover:bg-sky-700 text-white', tip: 'Attended but did modified training.' },
  { key: 'limited', label: 'Limited', active: 'bg-indigo-600 hover:bg-indigo-700 text-white', tip: 'Attended with participation restrictions.' },
  { key: 'excused', label: 'Excused', active: 'bg-blue-600 hover:bg-blue-700 text-white', tip: 'Approved / justified absence.' },
  { key: 'injured', label: 'Injured', active: 'bg-orange-600 hover:bg-orange-700 text-white', tip: 'Unavailable due to injury / medical reason.' },
  { key: 'absent', label: 'Absent', active: 'bg-red-600 hover:bg-red-700 text-white', tip: 'Did not attend.' },
  { key: 'no_show', label: 'No Show', active: 'bg-rose-700 hover:bg-rose-800 text-white', tip: 'Expected but failed to attend without notice.' },
];
const STATUS_LABEL = Object.fromEntries(ATTENDANCE_STATUS_OPTIONS.map(s => [s.key, s.label]));


const StaffTraining = ({ athleteId: propAthleteId }) => {
  const { user, hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isScopedMode = !!propAthleteId;
  const [scopedAthlete, setScopedAthlete] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagMap, setTagMap] = useState({});  // { athlete_id: [tag_id, ...] } — used to resolve target_tag_ids → athletes
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('schedule');
  const [weekOffset, setWeekOffset] = useState(0);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [markingAllPresent, setMarkingAllPresent] = useState(false);
  // Phase 74 — fixed roster snapshot loaded for the currently open attendance dialog.
  const [rosterAthleteIds, setRosterAthleteIds] = useState([]);
  const [rosterDerivedFrom, setRosterDerivedFrom] = useState(null);  // "snapshot" | "attendance_records" | "current_targeting"
  const [addAthletePickerOpen, setAddAthletePickerOpen] = useState(false);
  const [addAthleteQuery, setAddAthleteQuery] = useState('');
  const [addingAthlete, setAddingAthlete] = useState(false);

  // Attendance filter
  const [attendanceTypeFilter, setAttendanceTypeFilter] = useState('all');

  // Phase 85.3 — Repeat Sessions selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [repeatWizardOpen, setRepeatWizardOpen] = useState(false);

  const emptySession = { date: '', time: '', end_time: '', title: '', description: '', session_type: 'pitch', training_focus: 'General', target_type: 'all', target_squads: [], target_athletes: [], target_tag_ids: [] };
  const [formData, setFormData] = useState(emptySession);

  // Honor deep-link query params from the Dashboard Quick Actions bar:
  //   /training?tab=attendance  → open Attendance tab
  //   /training?new=1           → open Create Session dialog immediately
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'attendance' || tab === 'schedule') setActiveTab(tab);
    if (params.get('new') === '1') setCreateDialogOpen(true);
  }, [location.search]);

  useEffect(() => {
    fetchSessions();
    axios.get(`${API}/tags`).then(res => setTags(res.data || [])).catch(() => {});
    axios.get(`${API}/tags/map`).then(res => setTagMap(res.data || {})).catch(() => {});
    if (propAthleteId) {
      axios.get(`${API}/athletes/${propAthleteId}`).then(res => { setScopedAthlete(res.data); setAthletes([res.data]); }).catch(() => {});
    } else {
      axios.get(`${API}/athletes?status=active`).then(res => setAthletes(res.data)).catch(() => {});
    }
  }, [propAthleteId]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/training-sessions`);
      setSessions(response.data);
    } catch { toast.error('Failed to fetch sessions'); }
    finally { setLoading(false); }
  };

  // ===== Week calendar =====
  const weekDates = useMemo(() => {
    const base = new Date();
    base.setDate(base.getDate() + weekOffset * 7);
    return getWeekDates(base);
  }, [weekOffset]);

  const sessionsByDate = useMemo(() => {
    const map = {};
    sessions.forEach(s => {
      const key = s.date?.split('T')[0];
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [sessions]);

  const weekLabel = useMemo(() => {
    if (!weekDates.length) return '';
    const s = weekDates[0];
    const e = weekDates[6];
    const opts = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString('en-GB', opts)} - ${e.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
  }, [weekDates]);

  // ===== Handlers =====
  // Helper to format time for API (HH:MM -> HH:MM:SS)
  const formatTimeForApi = (time) => {
    if (!time) return time;
    // If already has seconds, return as-is
    if (time.split(':').length === 3) return time;
    // Add seconds
    return `${time}:00`;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        time: formatTimeForApi(formData.time),
        end_time: formData.end_time ? formatTimeForApi(formData.end_time) : null,
      };
      await axios.post(`${API}/training-sessions`, payload);
      toast.success('Session created');
      setCreateDialogOpen(false);
      setFormData(emptySession);
      fetchSessions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create session');
      toast.error(errorMsg);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedSession) return;
    try {
      const payload = {
        ...formData,
        time: formatTimeForApi(formData.time),
        end_time: formData.end_time ? formatTimeForApi(formData.end_time) : null,
      };
      const res = await axios.put(`${API}/training-sessions/${selectedSession.id}`, payload);
      if (res.status < 200 || res.status >= 300) throw new Error(`Unexpected status ${res.status}`);
      toast.success('Session updated');
      setEditDialogOpen(false);
      setSelectedSession(null);
      await fetchSessions();
    } catch (err) {
      console.error('[training] update failed:', err.response?.status, err.response?.data || err.message);
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update session');
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (session) => {
    if (!window.confirm(`Delete "${session.title}"? This will also remove all attendance records.`)) return;
    try {
      const res = await axios.delete(`${API}/training-sessions/${session.id}`);
      if (res.status !== 204 && (res.status < 200 || res.status >= 300)) throw new Error(`Unexpected status ${res.status}`);
      toast.success('Session deleted');
      await fetchSessions();
    } catch (err) {
      console.error('[training] delete failed:', err.response?.status, err.response?.data || err.message);
      toast.error(err.response?.data?.detail || 'Failed to delete session');
    }
  };

  const openEdit = (session) => {
    setSelectedSession(session);
    // HTML5 <input type="time"> expects HH:MM (not HH:MM:SS). Backend returns HH:MM:SS.
    const stripSeconds = (t) => (t && typeof t === 'string' ? t.slice(0, 5) : '');
    setFormData({
      date: session.date?.split('T')[0] || '',
      time: stripSeconds(session.time),
      end_time: stripSeconds(session.end_time),
      title: session.title || '',
      description: session.description || '',
      session_type: session.session_type || session.type || 'pitch',
      training_focus: session.training_focus || 'General',
      target_type: session.target_type || 'all',
      target_squads: session.target_squads || [],
      target_athletes: session.target_athletes || [],
      target_tag_ids: session.target_tag_ids || [],
    });
    setEditDialogOpen(true);
  };

  const openAttendance = (session) => {
    setSelectedSession(session);
    setRosterAthleteIds([]);
    setRosterDerivedFrom(null);
    setAddAthletePickerOpen(false);
    setAddAthleteQuery('');
    fetchSessionRoster(session.id);
    setAttendanceDialogOpen(true);
  };

  // Phase 85.2 — prefer the v2 roster endpoint which returns the frozen
  // participant snapshot + per-athlete `original_roster` + `added_via`
  // audit metadata. Falls back to the v1 (legacy) endpoint for any old
  // sessions the migration hasn't touched yet.
  const [rosterEntries, setRosterEntries] = useState([]);       // v2 metadata
  const [sessionLifecycle, setSessionLifecycle] = useState(null); // v2 state
  const [auditHistory, setAuditHistory] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [finalizeIssues, setFinalizeIssues] = useState([]);
  const canResolveOrphan = user?.role === 'Admin' || hasModuleAccess('attendance.resolve_orphan', 'edit');

  const fetchSessionRoster = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/training-sessions/${sessionId}/roster-v2`);
      const data = response.data || {};
      const participants = data.participants || [];
      setRosterEntries(participants);
      setRosterAthleteIds(participants.map(p => p.athlete_id));
      setRosterDerivedFrom('snapshot_v2');
      setSessionLifecycle({
        state: data.session_state,
        prepared_at: data.roster_prepared_at,
        completed_at: data.attendance_completed_at,
      });
      const map = {};
      participants.forEach(p => {
        if (p.attendance_status) map[p.athlete_id] = p.attendance_status;
      });
      setAttendanceRecords(map);
      setFinalizeIssues([]);
      return;
    } catch { /* fall through to legacy */ }
    try {
      const response = await axios.get(`${API}/training-sessions/${sessionId}/roster`);
      const data = response.data || {};
      const ids = Array.isArray(data.roster) ? data.roster : [];
      setRosterAthleteIds(ids);
      setRosterEntries(ids.map(id => ({ athlete_id: id, original_roster: true, added_via: 'legacy' })));
      setRosterDerivedFrom(data.derived_from || 'snapshot');
      setSessionLifecycle(null);
      const map = {};
      (data.by_athlete || []).forEach(e => {
        if (e?.status) map[e.athlete_id] = e.status;
      });
      setAttendanceRecords(map);
    } catch {
      try {
        const response = await axios.get(`${API}/training-sessions/${sessionId}/attendance`);
        const map = {};
        response.data.forEach(r => { map[r.athlete_id] = r.status; });
        setAttendanceRecords(map);
        setRosterAthleteIds(response.data.map(r => r.athlete_id));
        setRosterDerivedFrom('legacy_attendance_only');
      } catch { /* ignore */ }
    }
  };

  // Phase 85.2 — Prepare / Complete / Reopen actions.
  const prepareRoster = async () => {
    if (!selectedSession) return;
    try {
      await axios.post(`${API}/training-sessions/${selectedSession.id}/prepare-roster`);
      toast.success('Roster prepared and frozen.');
      await fetchSessionRoster(selectedSession.id);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare roster');
    }
  };
  const completeAttendance = async () => {
    if (!selectedSession) return;
    if (!window.confirm('Finalize attendance?\n\nOnce finalized, this record will be locked. Further changes will require an authorized user to reopen it.')) return;
    try {
      await axios.post(`${API}/training-sessions/${selectedSession.id}/attendance/complete`);
      toast.success('Attendance finalized & locked.');
      await fetchSessionRoster(selectedSession.id);
      fetchSessions();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object') {
        setFinalizeIssues(d.unresolved_participants || []);
        toast.error(`${d.message || 'Attendance is not complete'} — ${d.unresolved_count} athlete(s) unresolved. ${d.hint || ''}`);
      } else {
        toast.error(d || 'Failed to finalize attendance');
      }
    }
  };
  const saveAndFinishLater = async () => {
    // Marks autosave on every selection; nothing to finalize — just close.
    toast.success('Saved. Attendance kept In Progress — finish it later.');
    await fetchSessionRoster(selectedSession.id);
    fetchSessions();
  };
  const reopenAttendance = async () => {
    if (!selectedSession) return;
    const reason = window.prompt('Reason for reopening this finalized attendance?');
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A reason is required to reopen.'); return; }
    try {
      await axios.post(`${API}/training-sessions/${selectedSession.id}/attendance/reopen`, { reason: reason.trim() });
      toast.success('Attendance reopened.');
      await fetchSessionRoster(selectedSession.id);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen (requires authorization)');
    }
  };
  const loadAuditHistory = async () => {
    if (!selectedSession) return;
    try {
      const { data } = await axios.get(`${API}/training-sessions/${selectedSession.id}/attendance/audit`);
      setAuditHistory(data.history || []);
      setShowAudit(v => !v);
    } catch (err) {
      toast.error('Failed to load attendance history');
    }
  };
  const removeParticipant = async (athleteId, reason) => {
    if (!selectedSession) return;
    try {
      await axios.patch(
        `${API}/training-sessions/${selectedSession.id}/roster/participants`,
        { op: 'remove', athlete_id: athleteId, reason: reason || null },
      );
      toast.success('Athlete removed from this session.');
      await fetchSessionRoster(selectedSession.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const resolveOrphanParticipant = async (athleteId) => {
    if (!selectedSession) return;
    try {
      await axios.post(`${API}/training-sessions/${selectedSession.id}/attendance/resolve-orphan/${athleteId}`);
      toast.success('Stale roster reference resolved.');
      setFinalizeIssues(prev => prev.filter(item => item.athlete_id !== athleteId));
      await fetchSessionRoster(selectedSession.id);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resolve this participant');
    }
  };

  // Backward compat — old call-site still used in handleMarkAllPresent path.
  const fetchSessionAttendance = (sessionId) => fetchSessionRoster(sessionId);

  const handleMarkAttendance = async (athleteId, status) => {
    try {
      await axios.post(`${API}/training-sessions/${selectedSession.id}/attendance`, { athlete_id: athleteId, status });
      toast.success('Attendance marked');
      setAttendanceRecords(prev => ({ ...prev, [athleteId]: status }));
      // First mark on a legacy session freezes the snapshot server-side; reflect locally.
      if (!rosterAthleteIds.includes(athleteId)) {
        setRosterAthleteIds(prev => [...prev, athleteId].sort());
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Phase 74 — Admin/Staff can intentionally add an athlete to a session roster.
  const handleAddAthleteToRoster = async (athleteId) => {
    if (!selectedSession || !athleteId) return;
    setAddingAthlete(true);
    try {
      const res = await axios.post(
        `${API}/training-sessions/${selectedSession.id}/roster/add`,
        { athlete_id: athleteId }
      );
      const newRoster = res.data?.roster || rosterAthleteIds;
      setRosterAthleteIds(newRoster);
      toast.success('Athlete added to roster');
      setAddAthletePickerOpen(false);
      setAddAthleteQuery('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add athlete to roster');
    } finally {
      setAddingAthlete(false);
    }
  };

  // Phase 74 — when the attendance dialog is open, the roster is the FROZEN
  // snapshot returned by /api/training-sessions/{id}/roster, NEVER the live
  // tag/squad computation. We only fall back to the legacy tag-based resolver
  // for places that still need to estimate a roster outside the dialog (e.g.
  // schedule pill chip counts).
  const getSessionAthletes = (session, { useRoster = false } = {}) => {
    if (!session) return [];
    if (useRoster && selectedSession?.id === session.id && rosterAthleteIds.length > 0) {
      const byId = new Map(athletes.map(a => [a.id, a]));
      const entryById = new Map((rosterEntries || []).map(e => [e.athlete_id, e]));
      return rosterAthleteIds
        .map(id => {
          const master = byId.get(id);
          const e = entryById.get(id) || {};
          if (master) return { ...master, ...e, id, full_name: master.full_name };
          // Fall back to the canonical identity resolved server-side in the
          // frozen roster snapshot (roster-v2), so roster members outside the
          // current-season scope of the `athletes` master list still render
          // correctly (e.g. finalized read-only view after reopen/re-finalize).
          return {
            id,
            full_name: e.full_name || `Former athlete · ${id.slice(0, 8)}`,
            position: e.position,
            squad: e.squad,
            profile_photo: e.profile_photo,
            profile_photo_thumbnail: e.profile_photo_thumbnail,
            _missing: !e.full_name,
            identity_state: e.identity_state || 'missing',
            unresolved_reason: e.unresolved_reason,
            can_resolve_as_orphan: e.can_resolve_as_orphan,
          };
        })
        .filter(Boolean);
    }
    if (session.target_type === 'custom') {
      const tagIds = session.target_tag_ids || [];
      const tagIdSet = new Set(tagIds);
      return athletes.filter(a =>
        (session.target_squads || []).includes(a.squad) ||
        (session.target_athletes || []).includes(a.id) ||
        // Resolve target_tag_ids → athletes via the tag map. Without this, sessions
        // targeted purely by tag (target_squads/target_athletes empty) returned 0.
        (tagIds.length > 0 && (tagMap[a.id] || []).some(t => tagIdSet.has(t)))
      );
    }
    return athletes;
  };

  const handleMarkAllPresent = async () => {
    if (!selectedSession) return;
    // Phase 74 — operate on the FROZEN roster only.
    const sessionAthletes = getSessionAthletes(selectedSession, { useRoster: true });
    if (sessionAthletes.length === 0) {
      toast.info('No athletes in this session to mark.');
      return;
    }

    // Safety: if any existing records are non-present, confirm before overwriting
    const nonPresentExisting = sessionAthletes.filter(a => {
      const current = attendanceRecords[a.id];
      return current && current !== 'present';
    });
    if (nonPresentExisting.length > 0) {
      const ok = window.confirm(
        `${nonPresentExisting.length} athlete(s) already marked as Absent/Injured/Excused will be changed to Present. Continue?`
      );
      if (!ok) return;
    }

    setMarkingAllPresent(true);
    // Only hit the API for athletes not already marked present (idempotency + fewer writes)
    const toUpdate = sessionAthletes.filter(a => attendanceRecords[a.id] !== 'present');
    try {
      const results = await Promise.allSettled(
        toUpdate.map(a =>
          axios.post(`${API}/training-sessions/${selectedSession.id}/attendance`, {
            athlete_id: a.id, status: 'present'
          })
        )
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      // Optimistically update UI for successes
      setAttendanceRecords(prev => {
        const next = { ...prev };
        toUpdate.forEach((a, i) => { if (results[i].status === 'fulfilled') next[a.id] = 'present'; });
        return next;
      });
      if (failed === 0) {
        toast.success(`Marked ${succeeded} athlete(s) as Present`);
      } else {
        toast.warning(`Marked ${succeeded} Present; ${failed} failed — please retry those individually.`);
      }
    } catch (err) {
      toast.error('Failed to mark all present — please retry.');
    } finally {
      setMarkingAllPresent(false);
    }
  };

  const openCreateForDate = (dateStr) => {
    setFormData({ ...emptySession, date: dateStr });
    setCreateDialogOpen(true);
  };

  // Attendance view sessions
  const attendanceSessions = useMemo(() => {
    let filtered = [...sessions].sort((a, b) => b.date?.localeCompare(a.date));
    if (attendanceTypeFilter !== 'all') {
      filtered = filtered.filter(s => (s.session_type || 'pitch') === attendanceTypeFilter);
    }
    return filtered;
  }, [sessions, attendanceTypeFilter]);

  // ===== Session Form (shared between create and edit) =====
  // NOTE: Rendered as a function call (not a component) so that typing in
  // inputs doesn't remount the form on every parent re-render (focus loss fix).
  const renderSessionForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
        <div>
          <Label>Title *</Label>
          <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Tactical Session" required data-testid="session-title-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Session Type</Label>
            <Select value={formData.session_type} onValueChange={v => setFormData({ ...formData, session_type: v })}>
              <SelectTrigger data-testid="session-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SESSION_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${t.color}`} />{t.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Training Focus</Label>
            <Select value={formData.training_focus} onValueChange={v => setFormData({ ...formData, training_focus: v })}>
              <SelectTrigger data-testid="session-focus-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FOCUS_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Date *</Label>
            <DatePicker value={formData.date} onChange={v => setFormData({ ...formData, date: v })} testId="session-date-input" />
          </div>
          <div>
            <Label>Start Time *</Label>
            <Input type="time" value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} required data-testid="session-time-input" />
          </div>
          <div>
            <Label>End Time</Label>
            <Input type="time" value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} data-testid="session-end-time-input" />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Session details..." rows={2} data-testid="session-description-input" />
        </div>
        <TargetAudienceSelector
          athletes={athletes}
          tags={tags}
          value={{ target_type: formData.target_type, target_squads: formData.target_squads, target_athletes: formData.target_athletes, target_tag_ids: formData.target_tag_ids }}
          onChange={val => setFormData({ ...formData, target_type: val.target_type, target_squads: val.target_squads, target_athletes: val.target_athletes, target_tag_ids: val.target_tag_ids })}
          label="Target Audience"
          compact
          data-testid="session-target-audience"
        />
      </div>
      <DialogFooter className="mt-4">
        <Button type="submit" className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-session">{submitLabel}</Button>
      </DialogFooter>
    </form>
  );

  // SessionPill is defined at module scope (above StaffTraining) to prevent remounting on every parent render.

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="training-page">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Training" />

        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Training & Attendance
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">Plan sessions and track attendance</p>
          </div>
          <Button onClick={() => { setFormData(emptySession); setCreateDialogOpen(true); }} className="bg-pfap-green hover:bg-pfap-green/90 h-9 px-4" data-testid="create-session-button">
            <Plus className="w-4 h-4 mr-1.5" /> New Session
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-5">
            <TabsTrigger value="schedule" className="gap-1.5" data-testid="tab-schedule"><CalendarDays className="w-3.5 h-3.5" /> Schedule</TabsTrigger>
            <TabsTrigger value="attendance" className="gap-1.5" data-testid="tab-attendance"><ClipboardCheck className="w-3.5 h-3.5" /> Attendance</TabsTrigger>
          </TabsList>

          {/* ======== SCHEDULE TAB ======== */}
          <TabsContent value="schedule">
            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>
            ) : (
              <>
                {/* Selection toolbar (Phase 85.3) */}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2" data-testid="schedule-toolbar">
                  <div className="flex items-center gap-2">
                    {!selectionMode ? (
                      <Button size="sm" variant="outline" onClick={() => setSelectionMode(true)} data-testid="enter-selection-mode">
                        <CheckSquare className="w-3.5 h-3.5 mr-1.5" /> Select sessions
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => { setSelectionMode(false); setSelectedSessionIds([]); }} data-testid="exit-selection-mode">
                          Cancel
                        </Button>
                        <span className="text-xs text-gray-600 dark:text-gray-400" data-testid="selected-count">
                          {selectedSessionIds.length} selected
                        </span>
                      </>
                    )}
                  </div>
                  {selectionMode && (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setRepeatWizardOpen(true)}
                      disabled={selectedSessionIds.length === 0}
                      data-testid="open-repeat-wizard"
                    >
                      <Repeat className="w-3.5 h-3.5 mr-1.5" /> Repeat sessions
                    </Button>
                  )}
                </div>

                {/* Week navigation */}
                <div className="flex items-center justify-between mb-4">
                  <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w - 1)} data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></Button>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-testid="week-label">{weekLabel}</p>
                    {weekOffset !== 0 && (
                      <button onClick={() => setWeekOffset(0)} className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline">Today</button>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w + 1)} data-testid="next-week"><ChevronRight className="w-4 h-4" /></Button>
                </div>

                {/* Type legend */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {SESSION_TYPES.map(t => (
                    <div key={t.value} className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                      <span className={`w-2.5 h-2.5 rounded-full ${t.color}`} />{t.label}
                    </div>
                  ))}
                </div>

                {/* Weekly calendar grid — desktop shows all 7 days; on small
                    screens it horizontally scrolls with min-width per day so
                    session pills stay readable. */}
                <div className="overflow-x-auto -mx-1 px-1 pb-2">
                  <div className="grid grid-cols-7 gap-2 min-w-[720px] sm:min-w-0" data-testid="week-calendar">
                  {weekDates.map((date, i) => {
                    const key = formatDateKey(date);
                    const daySessions = sessionsByDate[key] || [];
                    const isToday = formatDateKey(new Date()) === key;
                    return (
                      <div key={key} className={`min-h-[140px] rounded-lg border p-2 transition-colors ${isToday ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`} data-testid={`day-${key}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div>
                            <p className={`text-[10px] font-medium uppercase ${isToday ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}>{DAY_NAMES[i]}</p>
                            <p className={`text-sm font-semibold ${isToday ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300'}`}>{date.getDate()}</p>
                          </div>
                          <button onClick={() => openCreateForDate(key)} className="w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 transition-colors" data-testid={`add-session-${key}`}>
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="space-y-1">
                          {daySessions.map(s => (
                            <SessionPill
                              key={s.id}
                              session={s}
                              onClick={() => openEdit(s)}
                              selectionMode={selectionMode}
                              selected={selectedSessionIds.includes(s.id)}
                              onToggleSelect={(sess) => {
                                setSelectedSessionIds(prev => (
                                  prev.includes(sess.id)
                                    ? prev.filter(x => x !== sess.id)
                                    : [...prev, sess.id]
                                ));
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* ======== ATTENDANCE TAB ======== */}
          <TabsContent value="attendance">
            {/* Type filter */}
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="attendance-type-filter">
              <button onClick={() => setAttendanceTypeFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${attendanceTypeFilter === 'all' ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`} data-testid="filter-all">All Types</button>
              {SESSION_TYPES.map(t => (
                <button key={t.value} onClick={() => setAttendanceTypeFilter(t.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${attendanceTypeFilter === t.value ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`} data-testid={`filter-${t.value}`}>
                  <span className={`w-2 h-2 rounded-full ${t.color}`} />{t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>
            ) : attendanceSessions.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><p className="text-gray-500 dark:text-gray-400">No sessions found for this category</p></CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="attendance-list">
                {attendanceSessions.map(session => {
                  const cfg = getTypeConfig(session.session_type);
                  const Icon = cfg.icon;
                  return (
                    <Card key={session.id} className={`border ${cfg.border} hover:shadow-sm transition-shadow`} data-testid={`attendance-card-${session.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                              <Icon className={`w-4 h-4 ${cfg.text}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{session.title}</p>
                                <Badge className={`text-[10px] px-1.5 py-0 ${cfg.bg} ${cfg.text} border ${cfg.border}`}>{cfg.label}</Badge>
                                {session.training_focus && session.training_focus !== 'General' && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">{session.training_focus}</Badge>
                                )}
                                {/* Phase 85.2 — Lifecycle chip on every schedule row (colour + icon + tooltip). */}
                                {session.session_state && (
                                  <SessionStatusBadge state={session.session_state} size="xs" />
                                )}
                              </div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                {new Date(session.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                {session.time ? ` at ${session.time.slice(0, 5)}` : ''}
                                {session.target_type === 'custom' && (session.target_squads?.length > 0) && ` · ${session.target_squads.join(', ')}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openAttendance(session)} data-testid={`mark-attendance-button-${session.id}`}>
                              <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Attendance
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(session)} data-testid={`edit-session-${session.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" onClick={() => handleDelete(session)} data-testid={`delete-session-${session.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== CREATE DIALOG ===== */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="create-session-dialog">
            <DialogHeader>
              <DialogTitle>Create Training Session</DialogTitle>
              <DialogDescription>Schedule a new session for your athletes</DialogDescription>
            </DialogHeader>
            {renderSessionForm(handleCreate, 'Create Session')}
          </DialogContent>
        </Dialog>

        {/* ===== REPEAT SESSIONS WIZARD (Phase 85.3) ===== */}
        <RepeatSessionsWizard
          open={repeatWizardOpen}
          onOpenChange={setRepeatWizardOpen}
          sourceSessions={sessions.filter(s => selectedSessionIds.includes(s.id))}
          onGenerated={() => {
            setSelectionMode(false);
            setSelectedSessionIds([]);
            fetchSessions();
          }}
        />

        {/* ===== EDIT DIALOG ===== */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="edit-session-dialog">
            <DialogHeader>
              <DialogTitle>Edit Training Session</DialogTitle>
              <DialogDescription>Update session details</DialogDescription>
            </DialogHeader>
            {renderSessionForm(handleUpdate, 'Save Changes')}
            <RepeatInformationCard recurrenceRules={selectedSession?.recurrence_rules} />
          </DialogContent>
        </Dialog>

        {/* ===== ATTENDANCE DIALOG ===== */}
        <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
          <DialogContent className="w-[96vw] max-w-6xl" data-testid="attendance-dialog">
            <DialogHeader className="text-left">
              <DialogTitle className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2" data-testid="attendance-dialog-title">
                {selectedSession && (() => { const cfg = getTypeConfig(selectedSession.session_type); const Icon = cfg.icon; return <div className="flex min-w-0 items-start gap-2"><div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${cfg.bg}`}><Icon className={`h-3.5 w-3.5 ${cfg.text}`} /></div><span className="break-words text-base leading-6" data-testid="attendance-session-title">{selectedSession.title}</span></div>; })()}
                {sessionLifecycle?.state && (
                  <SessionStatusBadge state={sessionLifecycle.state} />
                )}
              </DialogTitle>
              <DialogDescription className="flex flex-wrap gap-1 text-xs" data-testid="attendance-session-summary">
                <span data-testid="attendance-session-date">{selectedSession?.date && new Date(selectedSession.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                {selectedSession?.time && <><span aria-hidden="true">•</span><span data-testid="attendance-session-time">{selectedSession.time.slice(0, 5)}</span></>}
              </DialogDescription>
            </DialogHeader>

            {/* Phase 85.2 — Lifecycle action bar (Prepare / Complete / Reopen). */}
            {sessionLifecycle && (
              <div className="flex flex-wrap gap-2 pt-1 pb-2 border-b border-gray-100 dark:border-gray-800" data-testid="lifecycle-bar">
                {sessionLifecycle.state === 'scheduled' && (
                  <Button size="sm" variant="outline" onClick={prepareRoster} data-testid="prepare-roster-btn" className="h-7 text-xs">
                    Prepare Roster
                  </Button>
                )}
                {['roster_prepared', 'in_progress'].includes(sessionLifecycle.state) && (
                  <>
                    <Button size="sm" variant="outline" onClick={saveAndFinishLater} data-testid="save-finish-later-btn" className="h-7 text-xs">
                      Save & Finish Later
                    </Button>
                    <Button size="sm" onClick={completeAttendance} data-testid="finalize-attendance-btn" className="h-7 text-xs bg-pfap-green hover:opacity-90 text-white">
                      Finalize Attendance
                    </Button>
                  </>
                )}
                {sessionLifecycle.state === 'completed' && (
                  <>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300 self-center" data-testid="finalized-lock-label">Attendance Finalized 🔒</span>
                    {hasModuleAccess('attendance.reopen', 'edit') && (
                      <Button size="sm" variant="outline" onClick={reopenAttendance} data-testid="reopen-attendance-btn" className="h-7 text-xs">
                        Reopen Attendance
                      </Button>
                    )}
                  </>
                )}
                {sessionLifecycle.prepared_at && (
                  <span className="text-[10px] text-gray-400 self-center">
                    Roster frozen {new Date(sessionLifecycle.prepared_at).toLocaleString()}
                  </span>
                )}
                {(sessionLifecycle.state === 'completed' || auditHistory.length > 0) && (
                  <Button size="sm" variant="ghost" onClick={loadAuditHistory} data-testid="attendance-history-btn" className="h-7 text-xs">
                    {showAudit ? 'Hide History' : 'History'}
                  </Button>
                )}
              </div>
            )}
            {showAudit && (
              <div className="px-3 py-2 mt-1 rounded-md bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 text-[11px] space-y-1" data-testid="attendance-history-panel">
                <p className="font-semibold text-gray-600 dark:text-gray-300 mb-1">Attendance History</p>
                {auditHistory.length === 0 ? (
                  <p className="text-gray-400">No history yet.</p>
                ) : auditHistory.map((h, i) => (
                  <div key={i} data-testid={`history-row-${i}`}>
                    <span className="font-medium capitalize">{String(h.action || '').replace(/_/g, ' ')}</span>
                    {' — '}{h.performed_by_name || 'system'}{' · '}{h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}
                    {h.reason && <div className="text-gray-500 dark:text-gray-400 pl-2">Reason: {h.reason}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Phase 74 — provisional roster banner (legacy session without a snapshot). */}
            {rosterDerivedFrom === 'current_targeting' && (
              <div className="px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-300" data-testid="provisional-roster-banner">
                This legacy session has no fixed roster yet. The list below is provisional (based on current tags/squads). It will be frozen when you save the first attendance mark.
              </div>
            )}

            {finalizeIssues.length > 0 && (
              <div className="space-y-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid="attendance-unresolved-panel">
                <p className="font-semibold">Attendance cannot be finalized yet</p>
                {finalizeIssues.map((issue) => (
                  <div key={issue.athlete_id} className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 pt-2" data-testid={`unresolved-participant-${issue.athlete_id}`}>
                    <div>
                      <div className="font-medium" data-testid={`unresolved-name-${issue.athlete_id}`}>{issue.athlete_name}</div>
                      <div className="text-xs text-amber-800" data-testid={`unresolved-reason-${issue.athlete_id}`}>{issue.reason} · Status: {issue.attendance_status || 'Unmarked'}</div>
                    </div>
                    {issue.can_resolve_as_orphan && canResolveOrphan && (
                      <Button size="sm" variant="outline" onClick={() => resolveOrphanParticipant(issue.athlete_id)} data-testid={`resolve-orphan-${issue.athlete_id}`}>Resolve stale participant</Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Bulk action bar */}
            {(() => {
              const rosterAthletes = getSessionAthletes(selectedSession, { useRoster: true });
              const unmarkedCount = rosterAthletes.filter(a => !attendanceRecords[a.id]).length;
              const markedCount = rosterAthletes.length - unmarkedCount;
              const isFinalizedBar = sessionLifecycle?.state === 'completed';
              return rosterAthletes.length > 0 && !isFinalizedBar ? (
                <div className="flex items-center justify-between gap-2 px-1 pt-1 pb-2 border-b border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-300" data-testid="attendance-progress-label">
                    {markedCount} / {rosterAthletes.length} athletes marked
                    {unmarkedCount > 0
                      ? <span className="text-amber-600 dark:text-amber-400"> · {unmarkedCount} remaining</span>
                      : <span className="text-emerald-600 dark:text-emerald-400"> · Ready to finalize</span>}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAddAthletePickerOpen(prev => !prev)}
                      className="h-7 text-xs gap-1.5"
                      data-testid="add-athlete-to-roster-btn"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add athlete
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleMarkAllPresent}
                      disabled={markingAllPresent || rosterAthletes.length === 0}
                      className="h-7 text-xs gap-1.5"
                      data-testid="mark-all-present-btn"
                    >
                      <ClipboardCheck className="w-3.5 h-3.5" />
                      {markingAllPresent ? 'Marking…' : 'Mark All Present'}
                    </Button>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Phase 74 — inline athlete picker for "+ Add athlete" */}
            {addAthletePickerOpen && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 mb-2 bg-gray-50 dark:bg-gray-900/40" data-testid="add-athlete-picker">
                <Input
                  autoFocus
                  placeholder="Search athletes to add to this session…"
                  value={addAthleteQuery}
                  onChange={e => setAddAthleteQuery(e.target.value)}
                  className="h-8 text-xs"
                  data-testid="add-athlete-search-input"
                />
                <div className="max-h-40 overflow-y-auto mt-2 space-y-1">
                  {(() => {
                    const q = addAthleteQuery.trim().toLowerCase();
                    const rosterSet = new Set(rosterAthleteIds);
                    const candidates = athletes
                      .filter(a => !rosterSet.has(a.id))
                      .filter(a => !q || (a.full_name || '').toLowerCase().includes(q) || (a.squad || '').toLowerCase().includes(q))
                      .slice(0, 25);
                    if (candidates.length === 0) {
                      return <p className="text-[11px] text-gray-400 px-2 py-1">No matching athletes</p>;
                    }
                    return candidates.map(a => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => handleAddAthleteToRoster(a.id)}
                        disabled={addingAthlete}
                        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                        data-testid={`add-athlete-option-${a.id}`}
                      >
                        <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{a.full_name}</span>
                        <span className="text-[10px] text-gray-400">{a.position}{a.squad ? ` · ${a.squad}` : ''}</span>
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}

            <div className="space-y-3 py-2">
              {(() => {
                const rosterAthletes = getSessionAthletes(selectedSession, { useRoster: true });
                if (rosterAthletes.length === 0) {
                  return <p className="text-center text-gray-500 py-8" data-testid="attendance-empty">No athletes assigned to this session yet. Use &quot;Add athlete&quot; to populate the roster.</p>;
                }
                return (
                  <AthleteSearchFilter athletes={rosterAthletes} compact showSquadFilter={false} testIdPrefix="attendance-roster" label="Search roster">
                    {(filtered) => (
                      <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1" data-testid="attendance-roster-scroll">
                        {filtered.map(athlete => {
                          const current = attendanceRecords[athlete.id];
                          const isPending = !current;
                          const isFinalized = sessionLifecycle?.state === 'completed';
                          const isStaleIdentity = athlete.identity_state && athlete.identity_state !== 'active';
                          return (
                            <div key={athlete.id} className="grid gap-3 border p-3 sm:grid-cols-[minmax(220px,0.8fr)_minmax(0,1.7fr)] sm:items-center" data-testid={`attendance-athlete-${athlete.id}`}>
                              <div className="flex min-w-0 items-center gap-2.5 sm:sticky sm:left-0">
                                <AthleteAvatar
                                  photoUrl={athlete.profile_photo}
                                  thumbUrl={athlete.profile_photo_thumbnail}
                                  name={athlete.full_name}
                                  size="sm"
                                  className="flex-shrink-0"
                                  testId={`attendance-avatar-${athlete.id}`}
                                />
                                <div className="min-w-0">
                                  <p className="break-words text-sm font-medium leading-snug text-gray-900 dark:text-gray-100" data-testid={`attendance-athlete-name-${athlete.id}`}>{athlete.full_name}</p>
                                  <p className="text-xs text-gray-400">
                                    {athlete.position}{athlete.squad ? ` · ${athlete.squad}` : ''}
                                  </p>
                                </div>
                                {isPending && !isStaleIdentity && (
                                  <Badge className="ml-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 text-[9px] px-1.5 py-0" data-testid={`pending-badge-${athlete.id}`}>
                                    Pending
                                  </Badge>
                                )}
                                {/* Phase 85.2 — Manual-add badge. Surfaces
                                    when an athlete was added AFTER the
                                    roster was prepared. Admins can Remove
                                    them from this session only. */}
                                {(() => {
                                  const entry = rosterEntries.find(e => e.athlete_id === athlete.id);
                                  const isManual = entry && entry.original_roster === false;
                                  if (!isManual) return null;
                                  return (
                                    <Badge
                                      className="ml-1 bg-purple-100 text-purple-700 border border-purple-300 text-[9px] px-1.5 py-0"
                                      data-testid={`manual-badge-${athlete.id}`}
                                      title={
                                        `Manually added on ${entry.added_at ? new Date(entry.added_at).toLocaleString() : 'unknown'}`
                                        + (entry.added_reason ? ` — ${entry.added_reason}` : '')
                                      }
                                    >
                                      Manual
                                    </Badge>
                                  );
                                })()}
                              </div>
                              <div className="grid min-w-0 grid-cols-3 gap-1 sm:flex sm:flex-wrap sm:justify-end" data-testid={`status-controls-${athlete.id}`}>
                                {isStaleIdentity ? (
                                  <div className="col-span-3 flex flex-wrap items-center justify-end gap-2" data-testid={`stale-participant-${athlete.id}`}>
                                    <Badge className="border border-amber-300 bg-amber-100 text-amber-800" data-testid={`stale-reason-${athlete.id}`}>{athlete.unresolved_reason || 'Historical athlete reference'}{current ? ` · ${STATUS_LABEL[current] || current}` : ' · Unmarked'}</Badge>
                                    {!isFinalized && !current && athlete.can_resolve_as_orphan && canResolveOrphan && (
                                      <Button size="sm" variant="outline" onClick={() => resolveOrphanParticipant(athlete.id)} data-testid={`resolve-roster-orphan-${athlete.id}`}>Resolve</Button>
                                    )}
                                  </div>
                                ) : isFinalized ? (
                                  <Badge className="text-[11px] px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200 border" data-testid={`status-readonly-${athlete.id}`}>
                                    {current ? (STATUS_LABEL[current] || current) : 'Unresolved'}
                                  </Badge>
                                ) : ATTENDANCE_STATUS_OPTIONS.map(s => (
                                  <Button key={s.key} size="sm" variant={current === s.key ? 'default' : 'outline'} title={s.tip}
                                    className={`h-8 w-full px-2 text-[11px] sm:w-auto ${current === s.key ? s.active : ''}`}
                                    onClick={() => handleMarkAttendance(athlete.id, s.key)}
                                    data-testid={`mark-${s.key}-${athlete.id}`}
                                  >{s.label}</Button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </AthleteSearchFilter>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StaffTraining;
